import { Key } from "react";
import { Action, Dispatch } from "redux";
import { AxiosError, AxiosResponse } from "axios";
import { UploadProps, notification } from "antd";

import {
  ApiService,
  AuthorizedApiService,
  AuthorizedFormApiService,
  BlobApiService,
} from "../../../services/apiServices";
import {
  type MessagesType,
  type PackagePricingType,
  type RegisterFieldType,
} from "../../../models";
import {
  GET_PACKAGE_PRICING,
  GET_EVENT_IMAGE,
  GET_MESSAGES,
  IS_LOADING,
  GET_EVENT,
} from "./types";
import { getParticipants } from "../../Participants/modules/actions";

const setLoading = (loading: boolean) => ({
  type: IS_LOADING,
  payload: loading,
});

export const addEventInfo =
  (eventInfo: any) => async (dispatch: Dispatch<any>) => {
    try {
      dispatch(setLoading(true));

      AuthorizedApiService.post("/event", eventInfo)
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 201) {
            notification.error({
              message: "Something went wrong!",
            });
            dispatch(setLoading(false));
            return;
          }
          dispatch(setLoading(false));
          notification.success({
            message: "Event added!",
          });
          dispatch(getEventInfo());
        })
        .catch((err: AxiosError) => {
          dispatch(setLoading(false));
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      dispatch(setLoading(false));
      console.log(err);
    }
  };

export const updateEventInfo =
  (eventInfo: any, entityId: any) => async (dispatch: Dispatch<any>) => {
    try {
      dispatch(setLoading(true));

      AuthorizedApiService.put(`/event/${entityId}`, eventInfo)
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 200) {
            notification.error({
              message: "Something went wrong!",
            });
            dispatch(setLoading(false));
            return;
          }
          dispatch(setLoading(false));
          notification.success({
            message: "Event updated",
          });
          dispatch(getEventInfo());
        })
        .catch((err: AxiosError) => {
          dispatch(setLoading(false));
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      dispatch(setLoading(false));
      console.log(err);
    }
  };

export const updateIsComingSoon =
  (entityId: any, isComingSoon: any) => async (dispatch: Dispatch<any>) => {
    try {
      dispatch(setLoading(true));

      AuthorizedApiService.put(`/event/${entityId}`, { isComingSoon })
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 200) {
            notification.error({
              message: "Something went wrong!",
            });
            dispatch(setLoading(false));
            return;
          }
          dispatch(setLoading(false));
          notification.success({
            message: "Option Coming Soon updated",
          });
          dispatch(getEventInfo());
        })
        .catch((err: AxiosError) => {
          dispatch(setLoading(false));
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      dispatch(setLoading(false));
      console.log(err);
    }
  };

export const updateIsSoldOut =
  (entityId: any, isSoldOut: any) => async (dispatch: Dispatch<any>) => {
    try {
      dispatch(setLoading(true));

      AuthorizedApiService.put(`/event/${entityId}`, { isSoldOut })
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 200) {
            notification.error({
              message: "Something went wrong!",
            });
            dispatch(setLoading(false));
            return;
          }
          dispatch(setLoading(false));
          notification.success({
            message: "Option Sold Out updated",
          });
          dispatch(getEventInfo());
        })
        .catch((err: AxiosError) => {
          dispatch(setLoading(false));
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      dispatch(setLoading(false));
      console.log(err);
    }
  };

export const getEventInfo = () => async (dispatch: Dispatch<Action>) => {
  dispatch(setLoading(true));

  AuthorizedApiService.get("/event")
    .then((res: AxiosResponse) => {
      const { status, data } = res;
      if (status !== 200) {
        notification.error({
          message: "Something went wrong",
        });
        dispatch(setLoading(false));
        return;
      }
      dispatch(setLoading(false));
      dispatch({
        type: GET_EVENT,
        payload: data,
      });
    })
    .catch((err: AxiosError) => {
      dispatch(setLoading(false));
      notification.error({
        message: "Something went wrong",
      });
    });
};

export const getPackagePricing = () => async (dispatch: Dispatch<Action>) => {
  dispatch(setLoading(true));

  ApiService.get("package-pricing")
    .then((res: AxiosResponse) => {
      const { status, data } = res;
      if (status !== 200) {
        dispatch(setLoading(false));
        notification.error({
          message: "Something went wrong",
        });
        return;
      }
      dispatch(setLoading(false));
      dispatch({
        type: GET_PACKAGE_PRICING,
        payload: data,
      });
    })
    .catch((err: AxiosError) => {
      dispatch(setLoading(false));
      notification.error({
        message: "Something went wrong",
      });
    });
};

export const createPackagePricing =
  (packagePricing: any) => async (dispatch: Dispatch<any>) => {
    try {
      dispatch(setLoading(true));

      AuthorizedApiService.post("/package-pricing", packagePricing)
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 201) {
            notification.error({
              message: "Something went wrong!",
            });
            dispatch(setLoading(false));
            return;
          }

          dispatch(setLoading(false));
          if (packagePricing.package === "D") {
            notification.success({
              message: `Package prices created!`,
            });
            dispatch(getPackagePricing());
          }
        })
        .catch((err: AxiosError) => {
          dispatch(setLoading(false));
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      notification.error({
        message: "Something went wrong!",
      });
    }
  };

export const updatePackagePricing =
  (packagePricing: any, id: Key) => async (dispatch: Dispatch<any>) => {
    try {
      AuthorizedApiService.put(`/package-pricing/${id}`, packagePricing)
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 200) {
            notification.error({
              message: "Something went wrong!",
            });
            return;
          }
          notification.success({
            message: `Package ${packagePricing.package} updated!`,
          });
          dispatch(getPackagePricing());
        })
        .catch((err: AxiosError) => {
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      notification.error({
        message: "Something went wrong!",
      });
    }
  };

export const setMessages =
  (messages: any) => async (dispatch: Dispatch<any>) => {
    try {
      AuthorizedApiService.post("/messages", messages)
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 201) {
            notification.error({
              message: "Something went wrong!",
            });
            return;
          }
          notification.success({
            message: "Messages created!",
          });
          dispatch(getMessages());
        })
        .catch((err: AxiosError) => {
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      console.log(err);
    }
  };

export const getMessages =
  (entityId: null | string = null) =>
  async (dispatch: Dispatch<Action>) => {
    try {
      const url = entityId ? `/messages/${entityId}` : `/messages`;

      AuthorizedApiService.get(url)
        .then((res: AxiosResponse) => {
          const { status, data } = res;

          if (status !== 200) {
            notification.error({
              message: "Something went wrong",
            });
            return;
          }

          dispatch({
            type: GET_MESSAGES,
            payload: data[0] ? data[0] : data,
          });
        })
        .catch((err: AxiosError) => {
          notification.error({
            message: "Something went wrong",
          });
        });
    } catch (err) {
      console.log(err);
    }
  };

export const updateMessages =
  (messages: any, id: string | undefined) =>
  async (dispatch: Dispatch<any>) => {
    try {
      AuthorizedApiService.put(`/messages/${id}`, messages)
        .then((res: AxiosResponse) => {
          const { status, data } = res;

          if (status !== 200) {
            notification.error({
              message: "Something went wrong!",
            });
            return;
          }
          notification.success({
            message: "Messages updated!",
          });
          dispatch(getMessages());
        })
        .catch((err: AxiosError) => {
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      console.log(err);
    }
  };

export const addEventImage =
  (fileList: any, entityId: any, packageType: string) =>
  async (dispatch: Dispatch<any>) => {
    try {
      dispatch(setLoading(true));
      const imgUrl = new FormData();
      imgUrl.append("entityId", entityId);
      imgUrl.append("imgUrl", fileList[0].originFileObj);

      AuthorizedFormApiService.post(`/event/${packageType}`, imgUrl)
        .then((res: AxiosResponse) => {
          const { status } = res;

          if (status !== 201) {
            notification.error({
              message: "Something went wrong!",
            });
            dispatch(setLoading(false));
            return;
          }
          dispatch(setLoading(false));
          notification.success({
            message: "Image added!",
          });
          dispatch(getEventImagesById(entityId));
        })
        .catch((err: AxiosError) => {
          dispatch(setLoading(false));
          notification.error({
            message: "Something went wrong!",
          });
        });
    } catch (err) {
      dispatch(setLoading(false));
      console.log(err);
    }
  };

export const getEventImagesById =
  (entityId: string) => async (dispatch: Dispatch<Action>) => {
    try {
      AuthorizedApiService.get(`event/${entityId}`)
        .then((res: any) => {
          const { status, data } = res;

          if (status !== 200) {
            dispatch({
              type: GET_EVENT_IMAGE,
              payload: undefined,
            });

            if (res.response.data.message === "Event not found") {
              return;
            }

            notification.error({
              message: "Something went wrong",
            });
            return;
          }

          dispatch({
            type: GET_EVENT_IMAGE,
            payload: data,
          });
        })
        .catch((err: AxiosError) => {
          notification.error({
            message: "Something went wrong",
          });
        });
    } catch (err) {
      console.log(err);
    }
  };

export const exportFormat =
  (entityId: string, format: string, date: any = null) =>
  async (dispatch: Dispatch<Action>) => {
    try {
      BlobApiService.get(
        `participant/${entityId}/export/${format}${date ? `?date=${date}` : ""}`
      )
        .then((res: AxiosResponse) => {
          const { status, data } = res;

          if (status !== 200) {
            notification.error({
              message: "Something went wrong",
            });
            return;
          }

          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `present_${entityId}.${format}`);
          document.body.appendChild(link);
          link.click();
          notification.success({
            message: `Downloaded`,
          });
        })
        .catch((err: AxiosError) => {
          notification.error({
            message: "Something went wrong",
          });
        });
    } catch (err) {
      console.log(err);
    }
  };

export const exportInvoice =
  (startDate: any = null, endDate: any = null) =>
  async (dispatch: Dispatch<Action>) => {
    try {
      const api = `generate-invoices?${
        startDate ? `startDate=${startDate}` : ""
      }&${endDate ? `endDate=${endDate}` : ""}`;

      const response = await BlobApiService.get(api, {
        responseType: "blob",
        validateStatus: () => true, 
      });

      const { status, data } = response;

      if (status !== 200) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result as string;
            const json = JSON.parse(text);
            const message = json.message || json.error || text;

            notification.error({
              message: "Export failed",
              description: message,
            });
          } catch (e) {
            notification.error({
              message: "Export failed",
              description: "Unknown error occurred.",
            });
          }
        };

        reader.onerror = () => {
          notification.error({
            message: "Export failed",
            description: "Unable to read error response.",
          });
        };

        reader.readAsText(data);
        return;
      }

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "invoices.zip");
      document.body.appendChild(link);
      link.click();

      notification.success({
        message: `Downloaded`,
      });
    } catch (err: any) {
      console.error("Unexpected error:", err);
      const fallbackMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unknown error.";

      notification.error({
        message: "Export failed",
        description: fallbackMessage,
      });
    }
  };


export const generateAcreditations =
  (startDate?: any, endDate?: any) => async (dispatch: Dispatch<Action>) => {
    try {
      const api = `generate-accreditations?${
        startDate ? `startDate=${startDate}` : ""
      }&${endDate ? `endDate=${endDate}` : ""}`;

      BlobApiService.get(api)

        .then((res: AxiosResponse) => {
          const { status, data } = res;
          if (status !== 200) {
            notification.error({
              message: "Something went wrong",
            });
            return;
          }

          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "accreditations.zip");
          document.body.appendChild(link);
          link.click();
          notification.success({
            message: `Downloaded`,
          });
        })
        .catch((err: AxiosError) => {
          notification.error({
            message: "Something went wrong",
          });
        });
    } catch (err) {
      notification.error({
        message: "Something went wrong",
      });
    }
  };

export const resetDay = (entityId: any) => async (dispatch: Dispatch<any>) => {
  try {
    dispatch(setLoading(true));

    AuthorizedApiService.post("/reset-day", { entityId })
      .then((res: AxiosResponse) => {
        const { status, data } = res;

        if (status !== 200) {
          notification.error({
            message: "Something went wrong!",
          });
          dispatch(setLoading(false));
          return;
        }
        dispatch(setLoading(false));
        notification.success({
          message: `${data.message}`,
        });
        dispatch(getEventInfo());
        dispatch(getParticipants());
      })
      .catch((err: AxiosError) => {
        dispatch(setLoading(false));
        notification.error({
          message: "Something went wrong!",
        });
      });
  } catch (err) {
    dispatch(setLoading(false));
    console.log(err);
  }
};
